import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import RowOptions from "../../Widgets/RowOptions";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import { EducationFormValues } from "./interface";
import EducationModal, { EducationModalSaveValues } from "./EducationModal";
import useGetEducationHistory from "hooks/api/client/useGetEducationHistory";
import useCreateEducationHistory from "hooks/api/client/useCreateEducationHistory";
import useUpdateEducationHistory from "hooks/api/client/useUpdateEducationHistory";
import useDeleteEducationHistory from "hooks/api/client/useDeleteEducationHistory";
import { useGetClientById } from "hooks/api/useGetClientById";
import { useUpdateClient } from "hooks/api/client/useUpdateClient";

const BASE_SCHOOL_COLUMNS: DataTableColumn[] = [
  { key: "schoolName", label: "School Name" },
  { key: "teacherName", label: "Teacher's Name" },
  { key: "facilityType", label: "Facility Type" },
  { key: "educationalPlacement", label: "Educational Placement" },
  { key: "grade", label: "Grade" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "daysOfWeek", label: "Days of Week" },
  { key: "startTime", label: "Start Time" },
  { key: "endTime", label: "End Time" },
];

const EDIT_COLUMN: DataTableColumn = { key: "actions", label: "" };

const FACILITY_TYPE_LABELS: Record<string, string> = {
  SCHOOL: "School",
  DAYCARE: "Daycare",
  HOMESCHOOL: "Homeschool",
  VIRTUAL_SCHOOL: "Virtual school",
  NOT_YET_IN_SCHOOL_DAYCARE: "Not yet in school/daycare",
};

const EDUCATIONAL_PLACEMENT_LABELS: Record<string, string> = {
  FULLY_INTEGRATED: "Fully integrated",
  PARTIALLY_INTEGRATED: "Partially integrated",
  SELF_CONTAINED_NO_SUPPORT: "Self-contained (no support)",
  SELF_CONTAINED_WITH_1_1_SUPPORT: "Self-contained (1:1 support)",
  HOMESCHOOL: "Homeschool",
  NOT_YET_IN_DAYCARE_OR_SCHOOL: "Not yet in daycare or school",
};

const DAY_LABELS: Record<string, string> = {
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
  SUNDAY: "Sun",
};

const formatTimeDisplay = (time: string | null | undefined): string => {
  if (!time) return "-";
  const parsed = parse(time, "HH:mm", new Date());
  return isValid(parsed) ? format(parsed, "h:mm a") : "-";
};

const parseDateParts = (dateStr: string | null | undefined): [number, number, number] | null => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return [y, m, d];
};

const formatDate = (dateStr: string | null | undefined): string => {
  const parts = parseDateParts(dateStr);
  if (!parts) return "-";
  const [y, m, d] = parts;
  return `${String(m).padStart(2, "0")}/${String(d).padStart(2, "0")}/${y}`;
};

const toDateString = (date: Date | null): string | null => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseDateString = (dateStr: string | null | undefined): Date | null => {
  const parts = parseDateParts(dateStr);
  if (!parts) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
};

const EMPTY_VALUES: EducationFormValues = {
  specialEducationServices: "",
};

export const Education: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { educationHistory, refetchEducationHistory } = useGetEducationHistory(clientIdNum);

  const { data: clientData, refetch: refetchClient } = useGetClientById(clientIdNum);
  const { updateClient: updateClientMutation } = useUpdateClient({
    onSuccess: () => {
      refetchClient();
      setEditMode(false);
    },
  });

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createEducationHistoryMutation, isCreateEducationHistoryLoading } = useCreateEducationHistory({
    onSuccess: () => {
      refetchEducationHistory();
      closeModal();
    },
  });

  const { updateEducationHistoryMutation, isUpdateEducationHistoryLoading } = useUpdateEducationHistory({
    onSuccess: () => {
      refetchEducationHistory();
      closeModal();
    },
  });

  const { deleteEducationHistoryMutation } = useDeleteEducationHistory({
    onSuccess: () => {
      refetchEducationHistory();
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);
  const canEdit = true; // Going to replace it with actual permission check later

  const schoolColumns = canEdit ? [...BASE_SCHOOL_COLUMNS, EDIT_COLUMN] : BASE_SCHOOL_COLUMNS;
  const [formValues, setFormValues] = useState<EducationFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<EducationFormValues>(EMPTY_VALUES);

  useEffect(() => {
    const client = clientData?.data;
    if (client) {
      const mapped: EducationFormValues = {
        specialEducationServices: client.specialEducationIep || "",
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [clientData?.data]);

  const handleChange =
    (field: keyof EducationFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSaveText = () => {
    if (!clientId) return;
    updateClientMutation({
      clientId,
      data: { specialEducationIep: formValues.specialEducationServices || null },
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues);

  const rows: DataTableRow[] = educationHistory.map((item: any) => ({
    id: String(item.id),
    schoolName: item.schoolName || "-",
    teacherName: item.teacherName || "-",
    facilityType: FACILITY_TYPE_LABELS[item.facilityType] || item.facilityType || "-",
    educationalPlacement: EDUCATIONAL_PLACEMENT_LABELS[item.educationalPlacement] || item.educationalPlacement || "-",
    grade: item.grade || "-",
    daysOfWeek: Array.isArray(item.daysOfWeek) && item.daysOfWeek.length
      ? item.daysOfWeek.map((d: string) => DAY_LABELS[d] || d).join(", ")
      : "-",
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    startTime: formatTimeDisplay(item.startTime),
    endTime: formatTimeDisplay(item.endTime),
    _facilityType: item.facilityType || "",
    _educationalPlacement: item.educationalPlacement || "",
    _daysOfWeek: item.daysOfWeek || [],
    _startTime: item.startTime || "",
    _endTime: item.endTime || "",
    _startDate: item.startDate || null,
    _endDate: item.endDate || null,
  }));

  const parseTimeString = (time: string | null | undefined): Date | null => {
    if (!time) return null;
    const [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const handleModalSave = (values: EducationModalSaveValues) => {
    if (!clientIdNum) return;
    const payload = {
      schoolName: values.schoolName || null,
      teacherName: values.teacherName || null,
      grade: values.grade || null,
      facilityType: values.facilityType || null,
      educationalPlacement: values.educationalPlacement || null,
      daysOfWeek: values.daysOfWeek.length ? values.daysOfWeek : [],
      startTime: values.startTime || null,
      endTime: values.endTime || null,
      startDate: toDateString(values.startDate),
      endDate: toDateString(values.endDate),
    };

    if (selectedRow) {
      updateEducationHistoryMutation({ id: Number(selectedRow.id), payload });
    } else {
      createEducationHistoryMutation({ clientId: clientIdNum, payload });
    }
  };

  return (
    <SectionWrapper
      title="Education History"
      description="School placements, educational settings, and participation levels that inform learning environment considerations."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSaveText}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}

      <DataTable
        columns={schoolColumns}
        rows={rows}
        ariaLabel="Education history"
        emptyMessage="No school records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        sx={{ marginTop: 7 }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteEducationHistoryMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <EducationModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        isSaving={isCreateEducationHistoryLoading || isUpdateEducationHistoryLoading}
        initialValues={selectedRow ? {
          schoolName: selectedRow.schoolName !== "-" ? (selectedRow.schoolName as string) : "",
          teacherName: selectedRow.teacherName !== "-" ? (selectedRow.teacherName as string) : "",
          grade: selectedRow.grade !== "-" ? (selectedRow.grade as string) : "",
          facilityType: selectedRow._facilityType as string,
          educationalPlacement: selectedRow._educationalPlacement as string,
          daysOfWeek: selectedRow._daysOfWeek as string[],
          startTime: parseTimeString(selectedRow._startTime as string),
          endTime: parseTimeString(selectedRow._endTime as string),
          startDate: parseDateString(selectedRow._startDate as string),
          endDate: parseDateString(selectedRow._endDate as string),
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default Education;
